/**
 * Configuring one statistic per report, without a page of 379 rows.
 *
 * ADR-0055 publishes a field more than one report answers for once per report,
 * headed `<Statistic> - <Report>`. The request this file serves is the next
 * one: each of those columns configurable on its own, so `interceptions` in
 * the coverage report can be a *different statistic* from `interceptions` in
 * the passing report — a defender's caught ones against a quarterback's thrown
 * ones, with opposite Directions. That is the half of `pff-ingestion/18` which
 * ADR-0055 explicitly did not close, because Direction lives on the Stat
 * Dictionary entry: two Directions need two entries, which needs two
 * `stat_key`s, which needs a Binding scoped to each report.
 *
 * **So this is a layout problem, not a schema one.** Everything here writes the
 * shape `write_bindings` already accepts, and the storage — `Binding.feed_id` —
 * has been able to express it since ADR-0003.
 *
 * The layout the numbers force. Measured from the client's own
 * `metricNamesFBS.csv` on 2026-10-16: 13 reports, 248 distinct metric names,
 * **379** (report, metric) pairs, **67** metrics published by more than one
 * report. A section per report lists all 379 and makes the reviewer meet
 * `penalties` thirteen times to say the same thing thirteen times.
 *
 * **How much of the 67 is real work is not measured, and this file does not
 * pretend it is.** The nearest evidence is that the CSV's `low_best` column is
 * filled **10** times in 379 rows — so a Direction that has to be flipped is
 * rare. That is a reason to expect few overrides; it is *not* a count of them,
 * because a report can need its own statistic without differing in Direction.
 * The honest claim is only that the work is far smaller than the page, so:
 *
 * - the table stays **one row per field**, and a field several reports publish
 *   carries a count and a disclosure;
 * - expanding shows **one child per report**, each prefilled with the parent's
 *   statistic and marked inherited rather than chosen, so `penalties` stays one
 *   decision across thirteen reports instead of thirteen identical ones;
 * - each child shows **that report's own sample**, which is the evidence the
 *   decision rests on and which nothing on the screen showed before: 3,951
 *   passing yards against 143 rushing.
 *
 * **This supersedes the field review's Split and Merge buttons, and that
 * contradicts an accepted ADR.** ADR-0056's Consequences describe its *Value
 * from: every report* control as sitting "beside Split, on exactly the rows
 * Split was offered on", with `src/field-feeds.ts` owning the rules that keep
 * the two mutually exclusive. Split and Merge are gone as of this change, and
 * `src/field-feeds.ts` was never written — ADR-0056 is accepted and has no
 * implementation anywhere in either repository. So that paragraph describes a
 * screen that no longer exists, and the third answer it specifies still has a
 * place here: *one named report, plain header* is a further option in the
 * field's own Feed cell, beside "A statistic per Feed". Recorded in ADR-0056's
 * Amendment rather than left for whoever lands it to discover.
 *
 * Kept out of the components, like every other rule in this frontend, because
 * `npm test` is bare `node --experimental-strip-types --test` over `tests/` and
 * a rule inside a component is a rule nothing can assert.
 */

import type { Role } from "./api";
import type { ProblemRow } from "./binding-problems";

/**
 * A row of the Fields table, as these rules need it.
 *
 * A subset of the screen's own `FieldRow`, on the same terms as
 * `binding-problems.ts`: this file is asked questions about grouping and
 * scoping, and `reviewed`, `isNew` and `parseRule` have no bearing on either.
 */
export interface ReportRow {
  path: string;
  role: Role;
  statKey: string | null;
  /** The report this Binding reads, or null to read every one of them. */
  feedId: string | null;
  weight: number | null;
  /**
   * Where this field's column sits in the published header, or null for
   * nothing declared (ADR-0058).
   *
   * Read here because materialising a field has to carry it onto every report:
   * a field placed at position 3 and then given a statistic per report would
   * otherwise send all thirteen of its new columns to the end of the header,
   * which is the placement silently undone by the act of splitting it.
   */
  sortOrder: number | null;
  /** Which reports were found publishing this path, from the last read. */
  seenIn: string[];
  /**
   * What each report published for this path, keyed by Feed.
   *
   * Discovery already returns one `DiscoveredField` per `(feed, path)` with its
   * own samples, so this costs no endpoint. It is the difference between a
   * reviewer deciding from the field's name and deciding from its values.
   */
  samplesByFeed: Record<string, unknown[]>;
}

/**
 * One field, with every Binding that reads it.
 *
 * Generic in the row so the screen's own `FieldRow` survives a round trip
 * through here. These rules read a subset of a row and preserve the rest by
 * spreading it, and narrowing the type to that subset would have
 * `materialiseReports` silently return rows with no `reviewed`, no `samples`
 * and no `parse_rule` — the last of which the screen has already lost once
 * and published a file of forenames for.
 */
export interface FieldGroup<T extends ReportRow = ReportRow> {
  path: string;
  rows: T[];
  /**
   * Whether any of this field's Bindings names a report.
   *
   * The flag that says the per-report decision has been *taken*, which is what
   * keeps a worked field off the triage queue. Not "every row is scoped": a
   * path may legitimately carry an unscoped Binding and a scoped one, and that
   * half-materialised state is still a decision somebody made.
   */
  scoped: boolean;
  /** Every report's samples for this path, merged across the field's rows. */
  samplesByFeed: Record<string, unknown[]>;
}

/** One report's line under an expanded field. */
export interface ChildRow {
  feedId: string;
  statKey: string | null;
  role: Role;
  weight: number | null;
  /** This report's declared header position, or the field's where it inherits. */
  sortOrder: number | null;
  /**
   * True where this report has no Binding of its own and is showing the
   * field's single unscoped one.
   *
   * A screen-level affordance, never a stored state: nothing is written per
   * report until something is overridden. See `materialiseReports`.
   */
  inherited: boolean;
  /** This report's own first sample, or undefined where it published none. */
  sample: unknown;
}

/**
 * The fields, each with its Bindings, in the order the rows arrived in.
 *
 * Insertion order rather than sorted. A review is worked top to bottom and a
 * re-read preserves position, so sorting here would move six hundred rows under
 * the reader on a read nobody meant as a change.
 */
export function groupByField<T extends ReportRow>(rows: readonly T[]): FieldGroup<T>[] {
  const groups = new Map<string, FieldGroup<T>>();
  for (const row of rows) {
    let group = groups.get(row.path);
    if (!group) {
      group = { path: row.path, rows: [], scoped: false, samplesByFeed: {} };
      groups.set(row.path, group);
    }
    group.rows.push(row);
    if (row.feedId !== null) group.scoped = true;
    for (const [feedId, samples] of Object.entries(row.samplesByFeed)) {
      if (!(feedId in group.samplesByFeed)) group.samplesByFeed[feedId] = samples;
    }
  }
  return Array.from(groups.values());
}

/** One Feed, and what it was last seen publishing (ADR-0061). */
export interface PublishedFeed {
  feedId: string;
  /** Each path this Feed answered for, with the first few values it gave. */
  fields: Record<string, unknown[]>;
}

/** What a profile knows about one path without fetching anything. */
export interface FieldEvidence {
  seenIn: string[];
  samplesByFeed: Record<string, unknown[]>;
}

/**
 * Each path's reports and their samples, pivoted out of the stored per-Feed
 * reading.
 *
 * **This is what makes the disclosure available on load** — the whole of
 * `field-mapping-usability/03`. `seenIn` used to be fillable by a discovery run
 * and nothing else, so `fromBinding` set it empty and a field nobody had split
 * yet knew nothing about its own reports: on NCAA Division II football the
 * reviewer spent the serial re-read of seventeen leaderboards to reach the one
 * control that `Pct` and `Int` need, every time the page loaded. ADR-0061 keeps
 * that reading on the Feed, and this turns it back into the per-path shape the
 * screen asks in.
 *
 * Insertion order, so the reports under an expanded field are listed in the
 * profile's own Feed order — the endpoint sorts by `position` and this
 * preserves it, which is how the children match every other screen's ordering.
 *
 * A Feed that answered for a path with **no populated values** is still seen in
 * it. Presence and values are two facts: that report publishes the column, and
 * a report whose answer is empty is precisely the one worth looking at.
 */
export function evidenceByPath(
  feeds: readonly PublishedFeed[],
): Map<string, FieldEvidence> {
  const evidence = new Map<string, FieldEvidence>();
  for (const feed of feeds) {
    for (const [path, samples] of Object.entries(feed.fields)) {
      let held = evidence.get(path);
      if (!held) {
        held = { seenIn: [], samplesByFeed: {} };
        evidence.set(path, held);
      }
      if (!held.seenIn.includes(feed.feedId)) held.seenIn.push(feed.feedId);
      held.samplesByFeed[feed.feedId] = samples;
    }
  }
  return evidence;
}

/**
 * What the stored reading knows about one path, or nothing where it knows none.
 *
 * **The seeding rule itself**, which is the whole of `field-mapping-usability/03`:
 * `ReviewFields.fromBinding` builds every row through this, so a profile opened
 * cold arrives knowing its reports instead of with `seenIn: []`.
 *
 * Out here rather than in the component for the reason the header gives — a
 * rule inside a component is a rule nothing can assert — and this one in
 * particular, because the defect it fixes was a default buried in a row
 * constructor and the test that was supposed to cover it originally asserted a
 * hand-written stand-in that spread the whole row. That stand-in stayed green
 * against a broken constructor.
 *
 * Empty rather than absent, so a caller cannot accidentally leave `seenIn`
 * undefined on a row and have `reportsOf` throw on a field nobody has read.
 */
export function evidenceFor(
  evidence: ReadonlyMap<string, FieldEvidence>,
  path: string,
): FieldEvidence {
  return evidence.get(path) ?? { seenIn: [], samplesByFeed: {} };
}

/**
 * Forget what a field's reports were, because a fresh read found none.
 *
 * A re-read reads **every** Feed, so a path absent from the whole result is a
 * path no Feed publishes — authoritatively. The row was seeded on load from the
 * stored reading, which did list reports for it, and carrying those across
 * would leave the screen offering a disclosure onto reports it has just
 * measured as gone.
 *
 * The row survives; only its evidence goes. A field the publisher has dropped
 * is worth seeing, and its Feed cell then reads *No Feed publishes this* rather
 * than asking for the re-read that has only just happened.
 */
export function withoutReports<T extends ReportRow>(row: T): T {
  return { ...row, seenIn: [], samplesByFeed: {} };
}

/**
 * Whether this profile's reports are known well enough to call a path
 * unpublished.
 *
 * **`every`, deliberately, not `some`.** Saying *No Feed publishes this* is a
 * claim about all of them, and one Feed's reading cannot support it: a profile
 * created by Add Source has no reading for a Feed no scan shape covered, so a
 * path only that Feed publishes would be declared unpublished on the strength
 * of the sixteen that were read. A partly-read profile answers *unknown* and
 * asks for the read it genuinely needs.
 *
 * False for a profile with no Feeds at all, where `every` is vacuously true and
 * the question is meaningless.
 */
export function reportsKnown(feeds: readonly { readAt: string | null }[]): boolean {
  return feeds.length > 0 && feeds.every((feed) => feed.readAt !== null);
}

/**
 * Which reports this field has, from every kind of evidence there is.
 *
 * Three sources, and they answer for different states of a profile:
 *
 * - **the stored reading** — `seenIn`, seeded on load from each Feed's
 *   `published_fields` (ADR-0061). This is what a profile knows cold, and the
 *   ordinary case now that discovery records what it read;
 * - **a discovery just run** — the same field, refreshed in memory by a
 *   re-read, which is where it used to come from and nowhere else;
 * - **a field already split**, whose reports are recorded in its siblings'
 *   scopes. Still needed: it is the one case that survives even a Feed whose
 *   reading was never taken.
 *
 * Empty in two cases the screen has to tell apart, which is what `unreadNotice`
 * is for: nothing has read this profile's Feeds, or every Feed has been read and
 * none publishes this path. Neither offers a disclosure — no inference can
 * supply a list nobody has measured — and only the first is worth a re-read.
 */
export function reportsOf(group: FieldGroup<ReportRow>): string[] {
  const reports: string[] = [];
  const add = (feedId: string) => {
    if (!reports.includes(feedId)) reports.push(feedId);
  };
  for (const row of group.rows) {
    for (const feedId of row.seenIn) add(feedId);
    if (row.feedId !== null) add(row.feedId);
  }
  return reports;
}

/**
 * One line per report, each showing what that report would be read as.
 *
 * A report with a Binding of its own shows that Binding. A report without one
 * shows the field's unscoped Binding, marked inherited. That is what keeps the
 * work proportional: splitting all 67 duplicated fields by hand is 198
 * statistic choices, where inheriting leaves the reviewer touching only the
 * reports that differ.
 */
export function childRows(group: FieldGroup<ReportRow>): ChildRow[] {
  const unscoped = group.rows.find((row) => row.feedId === null) ?? null;
  return reportsOf(group).map((feedId) => {
    const own = group.rows.find((row) => row.feedId === feedId);
    const source = own ?? unscoped;
    return {
      feedId,
      statKey: source?.statKey ?? null,
      role: source?.role ?? "ignored",
      weight: source?.weight ?? null,
      sortOrder: source?.sortOrder ?? null,
      inherited: !own,
      sample: group.samplesByFeed[feedId]?.[0],
    };
  });
}

/**
 * Give every report its own Binding, so one of them can be overridden.
 *
 * **All of them, not the one being edited** — forced rather than chosen. An
 * unscoped Binding claims the key on every report, so one unscoped sitting
 * beside one scoped on the same path is refused as `colliding_column`
 * (ADR-0056). Materialising the whole field is the only shape the server
 * accepts, and doing it at the first override rather than at expansion is what
 * lets a reviewer open a field to look at it and change nothing.
 *
 * Three things are left alone, each of which would be a silent loss:
 *
 * - a field with **no reports known** — the cold-load case. Scoping to nothing
 *   binds the field to no report and publishes an empty column for every player;
 * - a field with **no unscoped Binding**, which is already materialised;
 * - a report some **scoped sibling already reads**, whose own statistic stands.
 *   Claiming it twice would put two rows on one Binding and drop one on save.
 *
 * The new rows replace the unscoped one **where it stood**, so a field does not
 * move to the bottom of six hundred rows the moment it is touched.
 *
 * **A note for whoever lands ADR-0056.** The copy is `{...row, feedId}`, so a
 * future `answeringFeedId` on the unscoped row would be carried onto every
 * scoped sibling — and a Binding holding both is refused as
 * `feed_scope_conflict`. That field has to be cleared here, and restored
 * deliberately in `collapseField`, which has the mirror of the problem.
 */
export function materialiseReports<T extends ReportRow>(
  rows: readonly T[],
  path: string,
  reports: readonly string[],
): T[] {
  if (reports.length === 0) return rows as T[];
  const unscoped = rows.find((row) => row.path === path && row.feedId === null);
  if (!unscoped) return rows as T[];

  const taken = new Set(
    rows.filter((row) => row.path === path && row.feedId !== null).map((row) => row.feedId),
  );
  const free = reports.filter((feedId) => !taken.has(feedId));
  if (free.length === 0) return rows as T[];

  const out: T[] = [];
  for (const row of rows) {
    if (row === unscoped) {
      for (const feedId of free) out.push({ ...row, feedId });
    } else {
      out.push(row);
    }
  }
  return out;
}

/**
 * Put a field back on one Binding across every report.
 *
 * The first sibling's role, statistic and weight stand for the single Binding
 * and the rest are discarded — which is what collapsing means, and why the
 * control says so before it is pressed.
 *
 * The reports and **their samples** survive on the merged row, so the field can
 * be expanded again without another three-minute re-read of thirteen Feeds.
 * Dropping the samples would make an undo cost what the original read cost.
 */
export function collapseField<T extends ReportRow>(rows: readonly T[], path: string): T[] {
  const siblings = rows.filter((row) => row.path === path);
  const head = siblings[0];
  if (!head) return rows as T[];

  const seenIn: string[] = [];
  const samplesByFeed: Record<string, unknown[]> = {};
  for (const row of siblings) {
    for (const feedId of row.seenIn) if (!seenIn.includes(feedId)) seenIn.push(feedId);
    if (row.feedId !== null && !seenIn.includes(row.feedId)) seenIn.push(row.feedId);
    for (const [feedId, samples] of Object.entries(row.samplesByFeed)) {
      if (!(feedId in samplesByFeed)) samplesByFeed[feedId] = samples;
    }
  }
  const merged: T = { ...head, feedId: null, seenIn, samplesByFeed };

  const out: T[] = [];
  let placed = false;
  for (const row of rows) {
    if (row.path !== path) {
      out.push(row);
    } else if (!placed) {
      placed = true;
      out.push(merged);
    }
  }
  return out;
}

/**
 * Drop an unscoped Binding that every report has already overridden.
 *
 * A field read apart has no field-level answer, and the screen says so: with no
 * unscoped row, `FieldGroupRows` renders *Each Feed below is read on its own*
 * in place of the Role, Statistic, Weight, position and Reviewed cells, because
 * showing the first report's answer there would read as the field's own and be
 * edited as if it were. An unscoped row that survives the split defeats that —
 * the field offers a full set of controls while `childRows` reports every
 * report as having its **own statistic**, so nothing on screen inherits the
 * answer being edited and the edit has no effect on the file.
 *
 * It is reachable from stored Bindings only, which is why this is normalisation
 * and not a bug fix in the editing path: `materialiseReports` consumes the
 * unscoped row as it scopes the reports, so no sequence of clicks produces one.
 * A profile saved with both keeps both (`_deduplicated`, deliberately), and the
 * commonest shape of it — *Statistic* with nothing chosen — marks the field with
 * a `stat_without_statistic` refusal whose only two remedies are the two
 * controls that should not have been there.
 *
 * **Only a row that would publish nothing**, which is the whole of the care this
 * needs. An unscoped `stat` row carrying a statistic is kept, controls and all,
 * even where every report is scoped: it is the one shape that still answers for
 * the Eligibility Rule and a per-90 denominator once the reports are read apart,
 * and a scoped Binding answers for neither (ADR-0056). Dropping `games_played`
 * there is the `no_players` failure `expansionHazard` warns about before the
 * split, reached by a route that would not warn at all. A `carried` row is kept
 * for the plainer reason that it writes a column of its own.
 *
 * **Read from `seenIn`, never from the scoped siblings.** Cold, a stored Binding
 * records which report it reads and not which reports publish its path — the
 * whole of `reportsOf`'s caution — so a profile whose Feeds include one this
 * path was never checked against would look fully covered by the scoped rows
 * alone, and the unscoped Binding that Feed is read by would be dropped on the
 * strength of knowing nothing about it. With `seenIn` empty this returns the
 * rows untouched and the field review waits for a re-read, as the Feed count
 * beside it already does.
 *
 * Silent, unlike the orphans `merge` keeps on the list. A path the publisher has
 * stopped sending is a fact about the source worth seeing; a row that publishes
 * no column is not, and the field itself stays on screen with every report under
 * it either way.
 */
export function dropSupersededUnscoped<T extends ReportRow>(rows: readonly T[]): T[] {
  const superseded = new Set<T>();
  for (const group of groupByField(rows)) {
    const unscoped = group.rows.find((row) => row.feedId === null);
    if (!unscoped || !publishesNothing(unscoped)) continue;
    // Discovery's evidence for this path, pooled across the field's rows the
    // way `merge` writes it — onto every row that reads the path, including the
    // scoped ones.
    const published = new Set<string>();
    for (const row of group.rows) for (const feedId of row.seenIn) published.add(feedId);
    if (published.size === 0) continue;
    const scoped = new Set(group.rows.map((row) => row.feedId));
    let covered = true;
    for (const feedId of published) if (!scoped.has(feedId)) covered = false;
    if (covered) superseded.add(unscoped);
  }
  if (superseded.size === 0) return rows as T[];
  return rows.filter((row) => !superseded.has(row));
}

/**
 * Whether this Binding would reach the file as no column at all.
 *
 * The two states a reviewer can leave a row in that publish nothing: *Ignore*,
 * and *Statistic* with no statistic chosen — which is not a third role but the
 * half-finished state of the one above it, and the state `stat_without_statistic`
 * is raised against. Every other role writes something, `carried` included.
 */
function publishesNothing(row: ReportRow): boolean {
  return row.role === "ignored" || (row.role === "stat" && row.statKey === null);
}

/**
 * How much per-report work this review holds, and how much of it is done.
 *
 * **`Values differ` is deliberately absent, and that is a refusal rather than
 * an omission.** The proposal this file implements asked for a second pill
 * counting the fields whose reports *disagree* — ADR-0055 measured 7 of PFF's
 * 22 shared columns — on the argument that it is "computable from what
 * discovery already returns: per-feed samples for a shared path". It is not,
 * and building it from the samples would reintroduce the exact mistake
 * ADR-0055 records as its first implementation's: **counting answers instead
 * of disagreements**.
 *
 * `DiscoveredPath.samples` is the first few *populated values in record order*
 * (`app/domain/payload.py`), gathered per Feed independently. Each report
 * orders its own records and covers its own players, so `samples[0]` from the
 * passing report and `samples[0]` from the rushing one are two different
 * players. Comparing them answers "did two reports publish different numbers
 * for different people", which is yes for almost everything — including
 * `grades_offense`, which ADR-0055 measured as disagreeing for **none** of its
 * players, because a player's offensive grade is one grade however many
 * reports repeat it.
 *
 * A disagreement is per *player*, so it needs the two reports' values keyed by
 * identity. Nothing in the discovery response carries that; the comparison
 * happens server-side during a Run. Shipping the pill anyway would send the
 * reviewer to the wrong fields with a number that looks measured, so the count
 * that can be computed honestly is shipped and the other is left for a
 * backend endpoint that can answer it.
 */
export interface TriageCounts {
  /** Fields more than one report publishes. The size of the page. */
  duplicated: number;
  /** Fields already given a statistic per report. How much of it is done. */
  perReport: number;
}

/** Whether more than one report publishes this field. */
export function isDuplicated(group: FieldGroup<ReportRow>): boolean {
  return reportsOf(group).length > 1;
}

/**
 * Whether this field has been given a statistic per report.
 *
 * **Exported beside the count rather than derived separately**, because the
 * triage pill and the list it filters to reading one predicate is the only
 * thing that stops them disagreeing — a pill saying 7 over a list of 22 is
 * worse than no pill.
 */
export function isPerReport(group: FieldGroup<ReportRow>): boolean {
  return isDuplicated(group) && group.scoped;
}

export function triageCounts(groups: readonly FieldGroup<ReportRow>[]): TriageCounts {
  let duplicated = 0;
  let perReport = 0;
  for (const group of groups) {
    if (isDuplicated(group)) duplicated += 1;
    if (isPerReport(group)) perReport += 1;
  }
  return { duplicated, perReport };
}

/** A composed Feed, as the scan's attribution needs to read it. */
export interface ShapedFeed {
  label: string;
  /** Which column set this Feed carries; null is one shape of its own. */
  shape?: string | null;
}

/**
 * How a Feed's shape is identified when deciding what to read, and what a read
 * then answers for.
 *
 * **One function, called by both halves**, and that is the whole reason it is
 * here rather than written out twice. `feedsToScan` uses it to pick the
 * addresses to read and `publishedFieldsByFeed` uses it to decide which Feeds a
 * read answers for — so the two disagreeing by a character would attribute one
 * report's field list to a Feed that publishes something else entirely. It was
 * duplicated once, in a function whose own docstring named that as the hazard.
 *
 * **An unshaped Feed is its own shape**, keyed by position. That is the safe
 * direction in both callers: it reads more addresses rather than fewer, and it
 * attributes a reading to nobody who did not earn it. `null` and `""` are both
 * "not declared".
 */
export function shapeKey(feed: ShapedFeed, index: number): string {
  return feed.shape ? `shape:${feed.shape}` : `feed:${index}`;
}

/** One address the scan actually read, and what it answered with. */
export interface ScannedAddress {
  label: string;
  fields: Record<string, unknown[]>;
}

/**
 * What each composed Feed should be recorded as publishing, from a scan that
 * read one address per shape.
 *
 * Add Source's half of ADR-0061, and it exists for one problem: `feedsToScan`
 * reads **one address per distinct shape**, so most of a Wyscout profile's
 * Feeds are never read at all — see `feedsToScan`, which owns that measurement.
 * Attributing a read to the Feeds that
 * share its shape is sound for the **paths** — that is the whole declared
 * meaning of a shape, "these Feeds publish the same fields" — and a lie for the
 * **samples**, which are the scanned conference's players and would appear on
 * screen under another conference's name.
 *
 * So a shape-mate is recorded as publishing the path with **no values**. The
 * disclosure is then complete and correctly counted, while every sample shown
 * is one the report named actually produced; the rest render as an em-dash
 * until something reads that Feed directly. Under-reporting the paths instead
 * would make a duplicated field look less duplicated, which is the one error
 * that loses the control this feature exists to offer.
 *
 * Keyed by label, which is what a `FeedInput` carries and what the server
 * already refuses duplicates of. Shape keys are recomputed exactly as
 * `feedsToScan` composes them — including *an unshaped Feed is its own shape* —
 * because the two reading that rule differently would silently cross-attribute.
 */
export function publishedFieldsByFeed(
  feeds: readonly ShapedFeed[],
  scanned: readonly ScannedAddress[],
): Record<string, Record<string, unknown[]>> {
  // Which shape each scanned address answered for. By label, because that is
  // the only thing the scan carries back that identifies the address it read.
  const readFor = new Map<string, Record<string, unknown[]>>();
  feeds.forEach((feed, index) => {
    const answer = scanned.find((address) => address.label === feed.label);
    if (answer) readFor.set(shapeKey(feed, index), answer.fields);
  });

  const out: Record<string, Record<string, unknown[]>> = {};
  feeds.forEach((feed, index) => {
    const read = readFor.get(shapeKey(feed, index));
    if (!read) return;
    const own = scanned.some((address) => address.label === feed.label);
    out[feed.label] = own
      ? read
      : Object.fromEntries(Object.keys(read).map((path) => [path, []]));
  });
  return out;
}

/** A report, as the matcher needs to name it. */
export interface ReportRef {
  feedId: string;
  label: string;
}

/**
 * A Stat Dictionary entry, as the matcher needs to read it.
 *
 * The **key** and nothing else. Matching on `display_name` was specified and
 * deliberately dropped: a display name is prose a human typed — "TDs Allowed
 * (Def. Coverage)" — and reading it would match on whichever report's name
 * happened to appear in a sentence. The key is the half somebody chose as an
 * identifier, and `cov_touchdowns` carries its report in a position this can
 * check rather than search.
 */
export interface MatchableEntry {
  key: string;
}

/**
 * Each report's own statistic, where the dictionary already carries one.
 *
 * The 2026-09-04 import of `metricNamesFBS.csv` created the report-scoped
 * entries this reads — `pass_interceptions`, `cov_interceptions`,
 * `cov_touchdowns`, `rec_touchdowns` — precisely because PFF's metric names
 * collide across reports. Offering them is what closes `pff-ingestion/18` with
 * four clicks instead of the data-surgery script that issue describes, and the
 * reviewer confirms rather than types.
 *
 * **Deliberately conservative, in three ways**, because a wrong suggestion here
 * is the bug it exists to prevent:
 *
 * - an entry qualifies only on its **key** — never its display name — and only
 *   if that key is this field's name under a prefix, so `cov_touchdowns` is
 *   never offered for `yards` on the strength of sharing a report;
 * - the **bare** entry is never suggested — `interceptions` is the ambiguous
 *   key whose single Direction ranks half the population backwards;
 * - a prefix matches a **single-word** report name, or the initials of a
 *   multi-word one. So `pass_` matches *Passing* and not *Pass Rush*, which are
 *   opposite sides of the line and four shared letters apart. A multi-word
 *   report with no initials entry gets no suggestion and is left for a human.
 *
 * Where two entries match one report the suggestion is withheld: an ambiguous
 * auto-match is a coin flip presented as an answer.
 */
export function autoMatchReports(
  path: string,
  reports: readonly ReportRef[],
  entries: readonly MatchableEntry[],
): Record<string, string> {
  const field = normalise(path.split(".").pop() ?? path);
  const suffix = `_${field}`;
  const candidates = entries
    .map((entry) => ({ entry, key: normalise(entry.key) }))
    .filter((candidate) => candidate.key.endsWith(suffix))
    .map((candidate) => ({
      entry: candidate.entry,
      prefix: candidate.key.slice(0, -suffix.length),
    }))
    .filter((candidate) => candidate.prefix.length > 0);

  const matched: Record<string, string> = {};
  for (const report of reports) {
    const hits = candidates.filter((candidate) => prefixNames(candidate.prefix, report.label));
    if (hits.length === 1) matched[report.feedId] = hits[0]!.entry.key;
  }
  return matched;
}

/** Whether a key's prefix is how this report's name would be abbreviated. */
function prefixNames(prefix: string, label: string): boolean {
  const words = normalise(label)
    .split("_")
    .filter((word) => word.length > 0);
  if (words.length === 0) return false;
  if (words.length === 1) return words[0]!.startsWith(prefix);
  return words.map((word) => word[0]).join("") === prefix;
}

/** Lowercase, with every run of non-alphanumerics as one underscore. */
function normalise(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * A reason this field must not be read per Feed, or null where there is none.
 *
 * **Every hazard here blocks.** There used to be a third answer — a
 * non-blocking note saying that scoping fixes the published header as
 * `<Statistic> - <Report>` for good. It was true, and it was removed: it
 * described a consequence of the thing the screen exists to offer, fired on
 * every ordinary field, and said nothing a reviewer could act on. So a hazard
 * being present now means exactly one thing, and `blocking` is gone with the
 * case that needed it.
 *
 * What remains are the two that empty the file. A scoped column **answers for
 * nothing** in the Eligibility Rule or as a per-90 denominator (ADR-0056), so
 * on a PFF profile scoping `games_played` has `across_reports` resolve
 * nothing, `_is_eligible` read every player as not having played, and the Run
 * fail `no_players` having dropped the division.
 *
 * The eligibility one now says what the field does *instead*, which is the half
 * a refusal cannot supply on its own. ADR-0057 publishes the participation
 * statistic as a single column carrying the largest answer any report gave, so
 * a reviewer looking at `games_played` on a thirteen-report profile sees a
 * count of thirteen, cannot split it, and would otherwise be left to guess
 * which of the thirteen reaches the file.
 */
export interface ExpansionHazard {
  message: string;
}

export interface HazardContext {
  statKey: string | null;
  /** The statistic this profile judges eligibility by, if it declares one. */
  eligibilityStatKey: string | null;
  entries: readonly { key: string; per_90_minutes_key: string | null }[];
}

export function expansionHazard({
  statKey,
  eligibilityStatKey,
  entries,
}: HazardContext): ExpansionHazard | null {
  if (!statKey) return null;

  if (eligibilityStatKey && statKey === eligibilityStatKey) {
    return {
      message:
        "This profile judges eligibility by this statistic, so it is published as one column holding the " +
        "largest answer any Feed gave — however many Feeds publish it. A Binding scoped to one Feed " +
        "answers for nothing there, so every player would read as not having played and the Run would " +
        "fail with no players. Give each Feed its own statistic only after the Eligibility Rule names a " +
        "different one.",
    };
  }

  // The denominator, not the numerator. `per_90_of` names the statistic being
  // rated, which publishes a column of its own and is unaffected by a scope;
  // `per_90_minutes_key` is the one `across_reports` has to resolve.
  const rating = entries.find((entry) => entry.per_90_minutes_key === statKey);
  if (rating) {
    return {
      message:
        `This statistic is the per-90 denominator for ${rating.key}. A Binding scoped to one Feed ` +
        "answers for nothing there, so every per-90 rate built on it would be left unscored.",
    };
  }

  // Nothing in the way. Scoping does rename the published header for good
  // (ADR-0055), and this used to say so — but that is a property of the
  // feature rather than a problem with this field, and a line that appears
  // against every ordinary statistic is noise a reviewer learns to scroll past.
  return null;
}

/**
 * Split a field across its reports and suggest each one's statistic.
 *
 * The suggestion is the step that makes this proportional to the work rather
 * than to the page: the September import already created `pass_interceptions`,
 * `cov_interceptions` and the rest, so a reviewer confirms four matches
 * instead of typing them.
 *
 * **It used to return a sentence as well**, shown as a banner over the table —
 * that the field now reads each report on its own, and how many reports a
 * statistic was found for. The screen says the first half itself, in the rows
 * the split puts on it and the badge on each one, so the banner restated what
 * it sat directly above and was asked for to go.
 *
 * **Only a scored field is matched.** `toDraft` writes `stat_key: null` for
 * every other role, so filing a match on a carried row would be a suggestion
 * that silently did nothing — and promoting the row to `stat` to make it stick
 * would change what the field means on the strength of a name match.
 */
export function matchReportsFor(
  group: FieldGroup<ReportRow>,
  reports: readonly ReportRef[],
  entries: readonly MatchableEntry[],
): Record<string, string> {
  const scored = group.rows.some((row) => row.role === "stat");
  return scored ? autoMatchReports(group.path, reports, entries) : {};
}

/**
 * What a field's Feed cell says when it is not offering the per-report choice.
 *
 * Null where the ordinary badge applies. What is worth a sentence is a
 * multi-Feed profile whose field knows no Feeds at all — `All Feeds` would
 * state as fact the thing the reviewer is deciding — and **nought reports has
 * two meanings**, which is the distinction ADR-0061 bought with `read_at`:
 *
 * - nothing has read this profile's Feeds, so the count is *unknown*. Worth the
 *   serial re-read, which is what the message asks for;
 * - every Feed has been read and none of them publishes this path. Nought means
 *   nought, and sending the reviewer off for three minutes to confirm it would
 *   change nothing. A Binding on a path the publisher has dropped is how a
 *   field reaches this state, and *Re-read to see Feeds* against it is an
 *   instruction that cannot help.
 *
 * `read` is whether any of the profile's Feeds carries a reading at all, not
 * whether this field appears in one — the latter is `reportCount` and asking it
 * twice would collapse the two cases back together.
 */
export function unreadNotice(
  reportCount: number,
  feedCount: number,
  read: boolean,
): string | null {
  if (reportCount > 0 || feedCount < 2) return null;
  return read ? "No Feed publishes this" : "Re-read to see Feeds";
}

/** One field's answer, as either Fields screen holds it before saving. */
export interface ReportChoice {
  role: Role;
  statKey: string | null;
  weight: number | null;
  /** Where this field's column sits in the published header (ADR-0058), or null
   *  for nothing declared — which sorts after everything declared. */
  sortOrder: number | null;
  parseRule: Record<string, unknown>;
}

/** A Binding as the create request carries one, naming its Feed by label. */
export interface LabelledDraft {
  path: string;
  role: Role;
  stat_key: string | null;
  parse_rule: Record<string, unknown>;
  weight: number | null;
  sort_order: number | null;
  reviewed: boolean;
  /** The Feed whose answer this is, or undefined to read every one of them. */
  feed_label?: string;
}

/**
 * The Bindings a field becomes, given what was decided about its reports.
 *
 * **Add Source's half of the per-report decision**, and the reason it needs a
 * shape of its own: on the create request the profile does not exist, so its
 * Feeds have no ids and a scope cannot be a `feed_id`. It is a `feed_label`,
 * which the server resolves once the Feeds are flushed — see
 * `_resolve_feed_labels`. The Fields screen has ids and keeps using them.
 *
 * Two outputs and no third:
 *
 * - **no override** — one Binding, unlabelled, exactly what this screen has
 *   always sent. A field somebody only looked at saves as though they had not,
 *   so expanding a disclosure cannot move a published header.
 * - **any override** — one Binding per report, the un-overridden ones carrying
 *   the field's own answer. Materialising all of them is forced rather than
 *   chosen: an unlabelled Binding claims the key on every report, so one of
 *   those beside one scoped Binding is refused as `colliding_column`.
 *
 * A report with no Binding of its own still gets one, and an override naming a
 * report the scan never saw is dropped rather than sent — the server would
 * refuse it as `unknown_feed` and take the whole profile down with it.
 */
export function draftsForField(
  path: string,
  base: ReportChoice,
  reports: readonly string[],
  overrides: Readonly<Record<string, ReportChoice>> | undefined,
): LabelledDraft[] {
  const scoped = overrides && reports.some((label) => overrides[label]);
  if (!scoped || reports.length < 2) return [draftOf(path, base)];
  return reports.map((label) => ({
    ...draftOf(path, overrides?.[label] ?? base),
    feed_label: label,
  }));
}

function draftOf(path: string, choice: ReportChoice): LabelledDraft {
  return {
    path,
    role: choice.role,
    // Only a scored field has a statistic, which is the rule the server
    // applies on the same line.
    stat_key: choice.role === "stat" ? choice.statKey : null,
    // Carried rather than emptied. A composed parse rule is how a publisher's
    // own spelling is read at all, and rebuilding it as `{}` published a
    // column of unread strings and a file of forenames.
    parse_rule: choice.parseRule,
    weight: choice.role === "stat" ? choice.weight : null,
    // **Not narrowed by role, unlike the two above.** A position is not a
    // scoring decision, and `stat` and `carried` are the two roles a reviewer
    // moves a field between while meaning its column to stay where it is.
    sort_order: choice.sortOrder,
    reviewed: choice.role !== "ignored",
  };
}


/**
 * How Add Source names one of a field's per-Feed lines.
 *
 * Exported because three things key off it — the child row, its "+ New"
 * statistic form, and the problem marked against it — and three inline
 * template literals that agree today are three that can stop agreeing. The
 * Fields screen keys by `(feed, path)` for the same reason and in the other
 * order; the two screens only need their own keys to be unique among their own
 * rows, so neither spelling is the canonical one.
 */
export function childRowKey(path: string, label: string): string {
  return `${path}::${label}`;
}

/**
 * The rows the live rules judge a field by, given what was decided about its
 * reports.
 *
 * **Derived from `draftsForField` rather than written beside it**, because the
 * two answering differently is the bug this exists to stop: a field read apart
 * saves one Binding per Feed and no unscoped one, while the screen went on
 * judging the field's own answer — and the commonest shape of that answer is
 * *Statistic* with nothing chosen, left behind by `Per Feed` on a field whose
 * every Feed has since been given a statistic. So the field was marked
 * `stat_without_statistic` and Save was blocked over a Binding that would never
 * be sent, pointing at controls the row deliberately no longer shows.
 *
 * Judging the drafts themselves is the only version of this that cannot drift:
 * whatever `draftsForField` decides to send is exactly what gets checked, down
 * to the `reports.length < 2` case where an override is held but one Feed makes
 * it moot.
 */
export function problemRowsForField(
  path: string,
  base: ReportChoice,
  reports: readonly string[],
  overrides: Readonly<Record<string, ReportChoice>> | undefined,
): ProblemRow[] {
  return draftsForField(path, base, reports, overrides).map((draft) => ({
    // The field's own row keeps the path as its key, which is what every
    // refusal, anchor and `travelTo` on that screen already names it by.
    key: draft.feed_label === undefined ? path : childRowKey(path, draft.feed_label),
    path: draft.path,
    role: draft.role,
    statKey: draft.stat_key,
    // The label, not an id: the Feeds do not exist yet, and it is what scopes
    // the Binding until `_resolve_feed_labels` turns it into one. Two Feeds
    // are two column spaces either way, which is all a collision needs.
    feedId: draft.feed_label ?? null,
    parseRule: draft.parse_rule,
  }));
}
